import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, call } from '@/lib/api';
import { queryClient } from '@/lib/query';
import type {
  ContactComment,
  ContactEvent,
  ContactImportNote,
  ContactMember,
} from '@/models/contacts';
import type { Lead, LeadComment, LeadEvent, PersonLevel } from '@/models/leads';

export type PersonStateCode =
  | 'qualified'
  | 'agent'
  | 'spam'
  | 'new'
  | 'in_work'
  | 'closed';

export const PERSON_STATE_LABELS: Record<PersonStateCode, string> = {
  qualified: 'Qualified',
  agent: 'Agent',
  spam: 'Spam',
  new: 'New request',
  in_work: 'In work',
  closed: 'Closed',
};

export const PERSON_STATE_COLORS: Record<PersonStateCode, string> = {
  qualified: 'blue',
  agent: 'grape',
  spam: 'red',
  new: 'orange',
  in_work: 'yellow',
  closed: 'gray',
};

export type PersonState = {
  contactId: number;
  state: PersonStateCode;
  openLead: { id: number; status: string; createdAt: string } | null;
  closed: { leadId: number; code: string; reason: string | null } | null;
};

export type PersonRequest = Lead & {
  write: boolean;
  isOpen: boolean;
  assigneeIsMember: boolean;
};

export type PersonCall = {
  id: number;
  leadId: number | null;
  contactId: number | null;
  direction: string;
  disposition: string;
  durationSeconds: number | null;
  answeredByEmail: string | null;
  answeredByName: string | null;
  createdAt: string;
};

export type PersonOffer = {
  id: number;
  uuid: string;
  title: string;
  accepted: boolean;
  acceptedAt: string | null;
  sentAt: string | null;
  archivedAt: string | null;
  createdAt: string;
};

export type PersonInvoice = {
  id: number;
  invoiceNumber: string;
  offerId: number;
  status: string;
  totalAmount: string;
  currency: string;
  createdAt: string;
};

export type PersonChannel = {
  id: number;
  kind: 'email' | 'phone' | 'whatsapp' | 'telegram';
  value: string;
  isPrimary: boolean;
  source: string;
};

export const ACTIVITY_KINDS = [
  'call',
  'whatsapp',
  'telegram',
  'meeting',
  'linkedin',
  'email',
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

export type PersonActivity = {
  id: number;
  contactId: number;
  leadId: number | null;
  kind: ActivityKind;
  status: 'open' | 'done';
  at: string;
  body: string | null;
  authorUserId: number | null;
  authorEmail: string | null;
  authorName: string | null;
  mondayAuthor: string | null;
};

export type PersonCardResponse = {
  person: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    whatsapp: string | null;
    telegram: string | null;
    qualified: boolean;
    qualifiedAt: string | null;
    lastContactAt: string | null;
    createdAt: string;
    updatedAt: string;
    notes?: string | null;
    origin?: string | null;
    mondayItemId?: string | null;
    referringAgentId?: number | null;
    referringAgentName?: string | null;
    sourceLeadId?: number | null;
    accountId?: number;
  };
  state: PersonState;
  access: {
    level: PersonLevel;
    parts: {
      header: boolean;
      allLeads: boolean;
      touches: boolean;
      money: boolean;
    };
    isMember: boolean;
    hasMembers: boolean;
    canRequestAccess: boolean;
    canEdit: boolean;
  };
  selectedLeadId: number | null;
  channels: PersonChannel[];
  members: ContactMember[];
  requests: PersonRequest[];
  hiddenRequests: { createdAt: string }[];
  feed: {
    personEvents: ContactEvent[];
    personComments: (ContactComment & { authorName?: string | null })[];
    requestEvents: LeadEvent[];
    requestComments: (LeadComment & { authorName?: string | null })[];
    calls: PersonCall[];
    activities: PersonActivity[];
    offers: PersonOffer[];
    invoices: PersonInvoice[];
  };
  account: {
    id: number;
    name: string;
    mondayStatus: string | null;
    mondayType: string | null;
  } | null;
  offers: PersonOffer[] | null;
  importNotes: ContactImportNote[];
};

export type PersonTarget =
  | { id: number }
  | { leadId: number }
  | { pulse: number };

export const personKey = (target: PersonTarget) =>
  'id' in target
    ? (['local-contacts', 'detail', Number(target.id), 'person'] as const)
    : 'leadId' in target
      ? (['local-contacts', 'detail', 'lead', Number(target.leadId)] as const)
      : (['local-contacts', 'detail', 'pulse', Number(target.pulse)] as const);

export function usePersonCard(target: PersonTarget) {
  return useQuery({
    queryKey: personKey(target),
    queryFn: () =>
      call<PersonCardResponse | { person: null; leadId: number }>(
        'persons/details',
        { method: 'GET', json: target },
      ).then((r) => r.json()),
    retry: false,
  });
}

const refreshPersons = () =>
  queryClient.invalidateQueries({ queryKey: ['local-contacts', 'detail'] });

const post = <T>(endpoint: string, json: any): Promise<T> =>
  call<T>(endpoint, { json }).then((r) => r.json());

export class ChannelTakenError extends Error {
  constructor(
    readonly contactId: number,
    readonly holderName: string | null,
  ) {
    super('The email belongs to another person');
  }
}

export function useAddChannel() {
  return useMutation({
    mutationFn: (body: { contactId: number; kind: string; value: string }) =>
      post<{ status: string }>('persons/channels/add', body).catch((e) => {
        if (e instanceof ApiError && e.status === 409) {
          const b = JSON.parse(e.body);
          throw new ChannelTakenError(Number(b.contactId), b.name ?? null);
        }
        throw e;
      }),
    onSuccess: refreshPersons,
  });
}

export function useRemoveChannel() {
  return useMutation({
    mutationFn: (channelId: number) =>
      post<{ ok: true }>('persons/channels/remove', { channelId }),
    onSuccess: refreshPersons,
  });
}

export function useAddActivity() {
  return useMutation({
    mutationFn: (body: {
      contactId: number;
      leadId?: number | null;
      kind: ActivityKind;
      status: 'open' | 'done';
      body?: string | null;
    }) => post<{ activity: PersonActivity }>('persons/activities/add', body),
    onSuccess: refreshPersons,
  });
}

export function useUpdateActivity() {
  return useMutation({
    mutationFn: (body: {
      id: number;
      status?: 'open' | 'done';
      body?: string;
    }) => post<{ activity: PersonActivity }>('persons/activities/update', body),
    onSuccess: refreshPersons,
  });
}

export type PersonView = 'my' | 'all';

export type PersonSort = 'name' | 'state' | 'owner' | 'created';

export type PersonListFilters = {
  view: PersonView;
  members?: 'none';
  search?: string;
  state?: PersonStateCode[];
  stage?: string[];
  ownerUserId?: number[];
  sort?: PersonSort;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
};

export type PersonListRow = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  telegram: string | null;
  accountId: number | null;
  createdAt: string;
  state: PersonStateCode;
  openLeadId: number | null;
  openLeadStatus: string | null;
  openLeadCreatedAt: string | null;
  closedCode: string | null;
  closedReason: string | null;
  nextTaskAt: string | null;
  openAssigneeUserId: number | null;
  openAssigneeName: string | null;
  members: {
    userId: number;
    name: string;
    email?: string | null;
    picture?: string | null;
  }[];
};

const toParams = (f: PersonListFilters) => {
  const out: Record<string, unknown> = { view: f.view };
  if (f.members) out.members = f.members;
  if (f.search) out.search = f.search;
  if (f.state?.length) out.state = f.state.join(',');
  if (f.stage?.length) out.stage = f.stage.join(',');
  if (f.ownerUserId?.length) out.ownerUserId = f.ownerUserId.join(',');
  if (f.sort) {
    out.sort = f.sort;
    out.dir = f.dir ?? 'asc';
  }
  if (f.page) out.page = f.page;
  if (f.pageSize) out.pageSize = f.pageSize;
  return out;
};

const get = <T>(endpoint: string, json?: any) =>
  call<T>(endpoint, { method: 'GET', json }).then((r) => r.json());

export function usePersonList(filters: PersonListFilters) {
  return useQuery({
    queryKey: ['persons', 'list', filters],
    queryFn: () =>
      get<{ rows: PersonListRow[]; total: number }>(
        'persons/list',
        toParams(filters),
      ),
  });
}

export type InboxRow = {
  id: number;
  contactId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  source: string;
  status: string;
  mqlStatus: string | null;
  isSpam: boolean;
  request: string | null;
  createdAt: string;
  personName: string | null;
  repeat: boolean;
};

export function useInbox(view: 'unassigned' | 'spam') {
  return useQuery({
    queryKey: ['persons', 'inbox', view],
    queryFn: () =>
      get<{ rows: InboxRow[]; total: number }>('persons/inbox', { view }),
  });
}

export type CasePerson = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  qualified: boolean;
  accountId: number | null;
  mondayItemId: string | null;
  createdAt: string;
  requests: number;
  offers: number;
  members: string[];
  state: PersonStateCode | null;
};

export type MergeCase = {
  id: number;
  code: string;
  outcome: string;
  proposal: Record<string, unknown> | null;
  status: string;
  comment: string | null;
  createdAt: string;
  items: { t: string; id: number | string; board?: string }[];
  people: CasePerson[];
};

export function useCases(code: string[]) {
  return useQuery({
    queryKey: ['persons', 'cases', code],
    queryFn: () =>
      get<{ rows: MergeCase[]; total: number }>('persons/cases', {
        code: code.join(','),
      }),
  });
}

export function usePersonCases(contactId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['persons', 'cases', 'contact', contactId],
    queryFn: () =>
      get<{ rows: MergeCase[]; total: number }>('persons/cases', {
        contactId,
      }),
    enabled,
  });
}

export function useDecideCase() {
  return useMutation({
    mutationFn: (body: {
      caseId: number;
      decision: 'same' | 'different';
      keepId?: number | null;
    }) => post<{ status: string }>('persons/cases/decide', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persons'] });
      refreshPersons();
    },
  });
}
